import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchTransactionsQuery } from "@/datasource/http/point-transactions/query";
import { cn } from "@/lib/utils";
import type { PointTransaction } from "@/models/point-transaction";

interface ReversedTransactionSelectProps {
  loyaltyAccountId: number;
  value?: string | null;
  onChange: (transactionId: string | null, transaction: PointTransaction | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ReversedTransactionSelect({
  loyaltyAccountId,
  value,
  onChange,
  disabled,
  placeholder,
}: ReversedTransactionSelectProps) {
  const { t } = useTranslation();

  const { data, isFetching } = useSearchTransactionsQuery({
    loyalty_account_id: loyaltyAccountId,
    per_page: 20,
  });

  const transactions = data?.data?.transactions ?? [];
  const selectedTransaction = transactions.find((tx) => tx.id === value) ?? null;

  function handleSelect(transaction: PointTransaction) {
    onChange(transaction.id, transaction);
  }

  function handleClear() {
    onChange(null, null);
  }

  if (!loyaltyAccountId) return null;

  if (value && selectedTransaction) {
    return (
      <Card className="relative">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleClear}
          disabled={disabled}
          className="absolute top-2 right-2 size-7"
        >
          <X className="size-3.5" />
        </Button>
        <CardContent>
          <TransactionRow transaction={selectedTransaction} selected />
        </CardContent>
      </Card>
    );
  }

  if (isFetching) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-3">
        {placeholder ?? t("transactions.create.select_original_transaction")}
      </p>
    );
  }

  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto">
      {transactions.map((transaction) => (
        <button
          key={transaction.id}
          type="button"
          disabled={disabled}
          onClick={() => handleSelect(transaction)}
          className={cn(
            "w-full text-left rounded-lg border p-3 transition-colors",
            "hover:bg-accent hover:border-accent-foreground/20",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <TransactionRow transaction={transaction} />
        </button>
      ))}
    </div>
  );
}

interface TransactionRowProps {
  transaction: PointTransaction;
  selected?: boolean;
}

function TransactionRow({ transaction, selected }: TransactionRowProps) {
  const { t } = useTranslation();
  const isPositive = transaction.points >= 0;

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm font-semibold truncate", selected && "text-foreground")}>
          {t(`transaction_types.${transaction.transaction_type}`)}
        </p>
        <p className="text-xs text-muted-foreground">
          {transaction.code} · {new Date(transaction.created_at).toLocaleDateString()}
        </p>
      </div>
      <span
        className={cn(
          "text-sm font-bold tabular-nums shrink-0",
          isPositive ? "text-green-600" : "text-foreground",
        )}
      >
        {isPositive ? "+" : ""}
        {transaction.points.toLocaleString()} pts
      </span>
    </div>
  );
}
